"use client";

import {
  FormEvent,
  useEffect,
  useState,
} from "react";
import {
  LoaderCircle,
  LocateFixed,
  Search,
} from "lucide-react";

import Footer from "@/components/layout/Footer";
import InstitutionalCta from "@/components/layout/InstitutionalCta";
import SupplierCard from "@/components/suppliers/SupplierCard";
import styles from "@/components/layout/public-pages.module.css";
import { apiRequest } from "@/lib/api";
import {
  trackSearchImpressions,
} from "@/lib/analytics";
import type { CompanyBusinessType } from "@/types/company";

type SupplierCategory = {
  name: string;
  slug: string;
};

export type Supplier = {
  id: number;
  business_type: CompanyBusinessType;
  trade_name: string;
  slug: string;
  custom_subdomain_active: boolean;
  custom_domain_active: string | null;
  description: string;
  city: string;
  state: string;
  district: string;
  whatsapp: string;
  website: string;
  logo_url: string | null;
  primary_color: string;
  categories: SupplierCategory[];
  products_count: number;
  rating_average: number | null;
  rating_count: number;
  public_mode: "presence" | "store";
  distance_km?: number | null;
  is_verified: boolean;
  verification_status:
    | "not_requested"
    | "pending"
    | "approved"
    | "rejected";
};

type SupplierListResponse = {
  count: number;
  results: Supplier[];
};

type UserLocation = {
  latitude: number;
  longitude: number;
};

function getInitials(name: string) {
  const words = name
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) {
    return "FN";
  }

  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }

  return (
    words[0][0] +
    words[words.length - 1][0]
  ).toUpperCase();
}

function getSupplierLocation(
  supplier: Supplier,
) {
  return (
    [supplier.city, supplier.state]
      .filter(Boolean)
      .join(", ") ||
    "Localização não informada"
  );
}

type FornecedoresClientProps = {
  initialSuppliers: Supplier[];
  initialSearch?: string;
  initialStorefrontUrls?: Record<number, string>;
};

export default function FornecedoresClient({
  initialSuppliers,
  initialSearch = "",
  initialStorefrontUrls,
}: FornecedoresClientProps) {
  const [inputValue, setInputValue] =
    useState(initialSearch);
  const [searchTerm, setSearchTerm] =
    useState(initialSearch);
  const [suppliers, setSuppliers] =
    useState<Supplier[]>(initialSuppliers);

  const [loading, setLoading] =
    useState(false);
  const [error, setError] =
    useState("");

  const [userLocation, setUserLocation] =
    useState<UserLocation | null>(null);

  const [radius, setRadius] =
    useState("25");

  const [locating, setLocating] =
    useState(false);

  const [locationError, setLocationError] =
    useState("");

  useEffect(() => {
    if (initialSuppliers.length === 0) {
      return;
    }

    trackSearchImpressions(
      initialSuppliers.map(
        (supplier) => supplier.slug,
      ),
      "",
    );
  }, [initialSuppliers]);

  useEffect(() => {
    if (!searchTerm && !userLocation) {
      setSuppliers(initialSuppliers);
      setLoading(false);
      setError("");
      return;
    }

    let isCancelled = false;

    async function loadSuppliers() {
      try {
        setLoading(true);
        setError("");

        const params =
          new URLSearchParams();

        if (searchTerm) {
          params.set(
            "search",
            searchTerm,
          );
        }

        if (userLocation) {
          params.set(
            "lat",
            String(userLocation.latitude),
          );
          params.set(
            "lng",
            String(userLocation.longitude),
          );
          params.set(
            "radius",
            radius,
          );
        }

        const query = params.toString();

        const data =
          await apiRequest<SupplierListResponse>(
            "/companies/public/" +
              (query ? "?" + query : ""),
          );

        if (!isCancelled) {
          setSuppliers(data.results);

          trackSearchImpressions(
            data.results.map(
              (supplier) => supplier.slug,
            ),
            searchTerm,
          );
        }
      } catch {
        if (!isCancelled) {
          setError(
            "Não foi possível carregar as empresas. Tente novamente.",
          );
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    }

    void loadSuppliers();

    return () => {
      isCancelled = true;
    };
  }, [
    searchTerm,
    userLocation,
    radius,
    initialSuppliers,
  ]);

  function handleSubmit(
    event: FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();
    setSearchTerm(inputValue.trim());
  }

  function clearSearch() {
    setInputValue("");
    setSearchTerm("");
  }

  function disableNearby() {
    setUserLocation(null);
    setLocationError("");
  }

  function enableNearby() {
    if (!navigator.geolocation) {
      setLocationError(
        "Seu navegador não oferece suporte à localização.",
      );
      return;
    }

    setLocating(true);
    setLocationError("");

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          latitude:
            position.coords.latitude,
          longitude:
            position.coords.longitude,
        });

        setLocating(false);
      },
      (geoError) => {
        setLocating(false);

        if (
          geoError.code ===
          geoError.PERMISSION_DENIED
        ) {
          setLocationError(
            "Permita o acesso à sua localização para encontrar empresas próximas.",
          );
          return;
        }

        setLocationError(
          "Não foi possível obter sua localização agora.",
        );
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000,
      },
    );
  }

  return (
    <div className={`${styles.page} ${styles.directory}`}>
      <section className="border-b border-blue-100 bg-blue-50/60 px-5 py-10 sm:px-8">
        <div className="mx-auto w-full max-w-[1440px]">
          <div className="max-w-3xl">
            <p className={styles.eyebrow}>Empresas no Fornecedor.Net</p>
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl">
              Encontre empresas perto de você
            </h1>

            <p className="mt-3 text-base leading-7 text-slate-500">
              Busque empresas, produtos e serviços e descubra fornecedores na sua região.
            </p>
          </div>

          <div className="mt-7 grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
            <form
              onSubmit={handleSubmit}
              className="flex min-w-0 items-center gap-3 rounded-lg border border-[#dce4ef] bg-white py-[6px] pl-4 pr-[6px]"
            >
              <Search
                size={20}
                className="shrink-0 text-[#65728b]"
                aria-hidden="true"
              />

              <input
                type="search"
                value={inputValue}
                onChange={(event) =>
                  setInputValue(
                    event.target.value,
                  )
                }
                placeholder="Empresa, produto ou cidade"
                className="min-w-0 flex-1 bg-transparent py-[10px] text-[13px] text-slate-800 outline-none placeholder:text-slate-400"
                aria-label="Buscar empresas"
              />

              <button
                type="submit"
                className="inline-flex min-h-[44px] shrink-0 items-center justify-center gap-[10px] rounded-[24px] bg-[linear-gradient(110deg,#0755f5,#8b19ef)] px-[22px] py-[11px] text-xs font-bold leading-[1.4] text-white transition-[filter] duration-200 hover:brightness-[1.08]"
              >
                Buscar
              </button>
            </form>

            <button
              type="button"
              onClick={
                userLocation
                  ? disableNearby
                  : enableNearby
              }
              disabled={locating}
              aria-pressed={Boolean(userLocation)}
              className={[
                "inline-flex h-12 items-center justify-center gap-2 rounded-xl border px-5 text-sm font-bold transition-colors",
                userLocation
                  ? "border-blue-600 bg-blue-600 text-white hover:bg-blue-700"
                  : "border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:text-blue-600",
                locating
                  ? "cursor-wait opacity-70"
                  : "",
              ].join(" ")}
            >
              {locating ? (
                <LoaderCircle
                  size={18}
                  className="animate-spin"
                />
              ) : (
                <LocateFixed size={18} />
              )}

              {locating
                ? "Localizando..."
                : userLocation
                  ? "Perto de mim ativo"
                  : "Perto de mim"}
            </button>
          </div>

          {userLocation && (
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span className="text-sm font-semibold text-slate-600">
                Mostrar empresas em até:
              </span>

              <select
                value={radius}
                onChange={(event) =>
                  setRadius(
                    event.target.value,
                  )
                }
                className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm font-bold text-slate-700 outline-none focus:border-blue-500"
                aria-label="Raio de busca"
              >
                <option value="5">5 km</option>
                <option value="10">10 km</option>
                <option value="25">25 km</option>
                <option value="50">50 km</option>
              </select>

              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700">
                <LocateFixed size={13} />
                Localização ativada
              </span>
            </div>
          )}

          {locationError && (
            <p className="mt-3 text-sm font-medium text-amber-700">
              {locationError}
            </p>
          )}
        </div>
      </section>

      <section aria-live="polite" aria-busy={loading} className="mx-auto w-full max-w-[1440px] px-5 py-8 sm:px-8">
        <div className="mb-6 flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
          <div>
            <h2 className="text-xl font-extrabold text-slate-950">
              Empresas encontradas
            </h2>

            {!loading && !error && (
              <p className="mt-1 text-sm text-slate-500">
                {suppliers.length}{" "}
                {suppliers.length === 1
                  ? "empresa disponível"
                  : "empresas disponíveis"}

                {userLocation &&
                  " no raio selecionado"}
              </p>
            )}
          </div>

          {searchTerm && (
            <button
              type="button"
              onClick={clearSearch}
              className="self-start text-sm font-bold text-blue-600 hover:text-blue-700 sm:self-auto"
            >
              Limpar busca
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex min-h-[260px] items-center justify-center">
            <div className="flex items-center gap-3 text-sm font-semibold text-slate-500">
              <LoaderCircle
                size={22}
                className="animate-spin text-blue-600"
              />
              Carregando empresas...
            </div>
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-6 py-12 text-center">
            <h2 className="text-lg font-bold text-red-700">
              Não foi possível carregar as empresas
            </h2>

            <p className="mt-2 text-sm text-red-600">
              {error}
            </p>
          </div>
        ) : suppliers.length > 0 ? (
          <div className="supplier-results grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {suppliers.map(
              (supplier) => (
                <SupplierCard
                  key={supplier.id}
                  initialStorefrontUrl={initialStorefrontUrls?.[supplier.id]}
                  name={
                    supplier.trade_name
                  }
                  location={getSupplierLocation(
                    supplier,
                  )}
                  slug={supplier.slug}
                  businessType={
                    supplier.business_type
                  }
                  initials={getInitials(
                    supplier.trade_name,
                  )}
                  logoUrl={
                    supplier.logo_url
                  }
                  productsCount={
                    supplier.products_count
                  }
                  ratingAverage={
                    supplier.rating_average
                  }
                  ratingCount={
                    supplier.rating_count
                  }
                  isVerified={
                    supplier.is_verified
                  }
                  distanceKm={
                    supplier.distance_km
                  }
                  publicMode={
                    supplier.public_mode
                  }
                  customSubdomainActive={
                    supplier.custom_subdomain_active
                  }
                  customDomainActive={
                    supplier.custom_domain_active
                  }
                />
              ),
            )}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center">
            <h2 className="text-xl font-bold text-slate-950">
              Nenhuma empresa encontrada
            </h2>

            <p className="mt-2 text-sm text-slate-500">
              {userLocation
                ? "Não encontramos empresas dentro desse raio. Tente aumentar a distância."
                : "Tente pesquisar por outro nome, categoria ou cidade."}
            </p>

            <div className="mt-5 flex flex-wrap justify-center gap-3">
              {userLocation && (
                <button
                  type="button"
                  onClick={() =>
                    setRadius("50")
                  }
                  className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-blue-700"
                >
                  Buscar em até 50 km
                </button>
              )}

              {searchTerm && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="rounded-xl border border-slate-300 bg-white px-5 py-2.5 text-sm font-bold text-slate-700 hover:border-blue-300 hover:text-blue-600"
                >
                  Limpar busca
                </button>
              )}
            </div>
          </div>
        )}
      </section>

      <InstitutionalCta
        title="Sua empresa também pode estar aqui."
        description="Crie sua presença digital na Fornecedor.net e conecte seu negócio a novos clientes."
        buttonText="Cadastrar minha empresa"
        buttonHref="/cadastro"
      />
      <Footer />
    </div>
  );
}
